import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import React, { useEffect } from 'react';
import { AppProvider } from '@/contexts/AppContext';
import './global.less';

// 替换清除图标为自定义图标
const replaceClearIcons = () => {
  const clearIconSelectors = [
    '.ant-input-clear-icon',
    '.ant-input-affix-wrapper .ant-input-clear-icon',
    '.ant-select-clear-icon',
    '.ant-picker-clear-icon',
  ];

  clearIconSelectors.forEach(selector => {
    const icons = document.querySelectorAll(selector);
    icons.forEach(icon => {
      // 检查是否已经替换过
      if (icon.querySelector('svg.icon[data-custom-icon="clear"]')) {
        return;
      }

      // 隐藏原来的图标
      const oldIcon = icon.querySelector('.anticon, svg:not(.icon)');
      if (oldIcon) {
        (oldIcon as HTMLElement).style.display = 'none';
      }

      // 创建新的 SVG 图标
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'icon');
      svg.setAttribute('width', '14');
      svg.setAttribute('height', '14');
      svg.setAttribute('data-custom-icon', 'clear');
      svg.setAttribute('aria-hidden', 'true');

      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttributeNS(
        'http://www.w3.org/1999/xlink',
        'xlink:href',
        '#icon-icon_qingchu'
      );
      svg.appendChild(use);

      icon.appendChild(svg);
    });
  });
};

// 替换日期选择器的日历图标为自定义图标
const replaceDatePickerIcons = () => {
  // DatePicker 的日历图标选择器
  const dateIconSelectors = [
    '.ant-picker-suffix .anticon',
    '.ant-picker .anticon-calendar',
  ];

  dateIconSelectors.forEach(selector => {
    const icons = document.querySelectorAll(selector);
    icons.forEach(icon => {
      // 检查是否已经替换过
      if (
        (icon as HTMLElement)
          .closest('.ant-picker-suffix')
          ?.querySelector('svg.icon[data-custom-icon="calendar"]')
      ) {
        return;
      }

      // 找到父容器（通常是 .ant-picker-suffix）
      const suffix = (icon as HTMLElement).closest('.ant-picker-suffix');
      if (!suffix) {
        return;
      }

      // 隐藏原来的图标
      (icon as HTMLElement).style.display = 'none';

      // 检查是否已经添加过自定义图标
      if (suffix.querySelector('svg.icon[data-custom-icon="calendar"]')) {
        return;
      }

      // 创建新的 SVG 图标
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'icon');
      svg.setAttribute('width', '14');
      svg.setAttribute('height', '14');
      svg.setAttribute('data-custom-icon', 'calendar');
      svg.setAttribute('aria-hidden', 'true');

      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttributeNS(
        'http://www.w3.org/1999/xlink',
        'xlink:href',
        '#icon-icon_rili'
      );
      svg.appendChild(use);

      suffix.appendChild(svg);
    });
  });
};

// 使用 MutationObserver 监听 DOM 变化
const setupIconObserver = () => {
  const observer = new MutationObserver(() => {
    replaceClearIcons();
    replaceDatePickerIcons();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return observer;
};

const theme = {
  token: {
    colorPrimary: '#2E74FF',
    colorSuccess: '#28B94F',
    colorWarning: '#FFA10B',
    colorError: '#F54242',
    colorPrimaryHover: '#4382FF',
    colorPrimaryActive: '#2968E5',
  },
  components: {
    Button: {
      controlHeightSM: 32,
      controlHeight: 32,
      controlHeightLG: 40,
      borderRadius: 4,
    },
    Table: {
      cellPaddingInline: 14,
      cellPaddingBlock: 14,
      cellPaddingInlineSM: 10,
      cellPaddingBlockSM: 10,
      rowHoverBg: '#F7F7FB',
      headerBg: '#F7F7FB',
      headerBorderRadius: 0,
      headerSplitColor: '#F7F7FB',
      headerColor: '#575A69',
    },
    Pagination: {
      itemActiveColor: '#165DFF',
    },
  },
};

export function rootContainer(container: React.ReactElement) {
  // 组件挂载后设置图标替换
  const RootWrapper = ({ children }: { children: React.ReactElement }) => {
    const [antdLocale, setAntdLocale] = React.useState(() => {
      const currentLocale = localStorage.getItem('umi_locale') || 'en-US';
      return currentLocale === 'zh-CN' ? zhCN : enUS;
    });

    useEffect(() => {
      // 初始替换
      replaceClearIcons();
      replaceDatePickerIcons();

      // 设置观察器监听 DOM 变化
      const observer = setupIconObserver();

      // 监听语言变化（通过自定义事件）
      const handleLanguageChange = () => {
        const locale = localStorage.getItem('umi_locale') || 'en-US';
        setAntdLocale(locale === 'zh-CN' ? zhCN : enUS);
      };

      // 监听 storage 变化（跨标签页）
      window.addEventListener('storage', handleLanguageChange);
      // 监听自定义事件（同标签页内）
      window.addEventListener('languagechange', handleLanguageChange);

      // 定期检查语言变化（作为备用方案）
      const intervalId = setInterval(() => {
        const locale = localStorage.getItem('umi_locale') || 'en-US';
        const expectedLocale = locale === 'zh-CN' ? zhCN : enUS;
        if (antdLocale !== expectedLocale) {
          setAntdLocale(expectedLocale);
        }
      }, 100);

      return () => {
        observer.disconnect();
        window.removeEventListener('storage', handleLanguageChange);
        window.removeEventListener('languagechange', handleLanguageChange);
        clearInterval(intervalId);
      };
    }, [antdLocale]);

    return React.createElement(
      ConfigProvider,
      { locale: antdLocale, theme: theme },
      children
    );
  };

  return React.createElement(
    AppProvider,
    null,
    React.createElement(RootWrapper, null, container)
  );
}
